/**
 * Blocks navigation surfaces the signed-in user has no permission for.
 *
 * Hiding a sidebar entry is presentation. This is what stops the same page
 * loading when the URL is typed, pasted from a colleague, or restored from a
 * bookmark — the cases that actually happen with an external partner account.
 *
 * It is still not the lock: the API refuses these requests independently. This
 * exists so a denied user sees an honest message instead of a page of failed
 * requests and empty charts.
 */
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { hasPermission } from '@/lib/permissions';
import { requiredPermissionForPath } from '@/lib/navAccess';

export function RouteAccessGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user, isLoading } = useAuth();

  // Say nothing until identity is known. Rendering the denial first would
  // flash "no access" at every user on a slow /me.
  if (isLoading || !user) return <>{children}</>;

  const required = requiredPermissionForPath(location.pathname);
  if (!required || hasPermission(user, required)) return <>{children}</>;

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="max-w-md rounded-lg border border-[var(--border-color)] bg-[var(--surface)] p-8 text-center">
        <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--surface-hover)]">
          <Lock className="h-5 w-5 text-[var(--text-muted)]" aria-hidden="true" />
        </div>
        <h1 className="text-base font-semibold text-[var(--text-primary)]">
          You don&rsquo;t have access to this page
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-[var(--text-muted)]">
          Your account can&rsquo;t open this part of Silas. If you think it should, ask the account
          owner to update your access.
        </p>
      </div>
    </div>
  );
}
